import { useEffect, useState } from 'react';

import { Trans } from '@lingui/react/macro';
import { LinearProgress } from '@mui/material';
import type { ExtractAtomValue } from 'jotai';
import { useSetAtom } from 'jotai';
import { Provider } from 'react-redux';

import type { PatientDownloadableAtoms } from '@/modules/annotator/casePresentation/patientDownloadable/hooks/usePatientDownloadableSettings';
import type { PublicPatientInfo } from '@/react-query/usePublicPatientDownloadable';

import { VideaTypography } from '../../../common-components/elements/VideaTypography/VideaTypography';
import {
  latestImagesVisitDateAtom,
  shouldDisplayRootTipsAtom,
  viewerModeAtom,
} from '../../../modules/annotator/atoms';
import {
  hiddenAnnotationsAtom,
  patientDownloadableNotesAtom,
  patientDownloadableSelectedTreatmentsAtom,
  selectedFindingImageIdAtom,
  selectedFindingsAtom,
  selectedTeethPreviewAtom,
} from '../../../modules/annotator/casePresentation/atoms';
import {
  modelsTogglesAtom,
  toothPartsTogglesAtom,
} from '../../../modules/annotator/findingSidebar/atoms';
import {
  binFilterAtom,
  confidenceScoreFilterAtom,
} from '../../../modules/annotator/findingSidebar/confidenceScoreFilter/atoms';
import {
  brightnessValueAtom,
  contrastValueAtom,
  enabledHDAtom,
  hideFindingsAtom,
  isOpacityInvertedAtom,
  previewDisplayDeviceAtom,
  previewModeAtom,
  showSegmentationAtom,
} from '../../../modules/annotator/multiFileViewer/imageToolbar/atoms';
import { selectedAnalysesAtom } from '../../../modules/patientList/atoms';
import { configureStore } from '../../../store/configureStore';
import { blueBlack630 } from '../../../theme/videa-style-tokens';
import type { PracticeFeatureFlagKey } from '../../../utils/user';
import {
  selectedUnifiedIdAtom,
  selectedVisitAtom,
} from '../../appHeader/imageViewerMenu/atoms';

const useSetAtoms = (atomsFromQRCode: PatientDownloadableAtoms | undefined) => {
  // Set atom values for findings display
  const setVisitDate = useSetAtom(latestImagesVisitDateAtom);
  const setModelsToggles = useSetAtom(modelsTogglesAtom);
  const setHideFindings = useSetAtom(hideFindingsAtom);
  const setEnabledHD = useSetAtom(enabledHDAtom);
  const setShowSegmentation = useSetAtom(showSegmentationAtom);
  const setBrightnessValue = useSetAtom(brightnessValueAtom);
  const setContrastValue = useSetAtom(contrastValueAtom);
  const setIsOpacityInverted = useSetAtom(isOpacityInvertedAtom);
  const setToothPartsToggles = useSetAtom(toothPartsTogglesAtom);
  const setBinFilter = useSetAtom(binFilterAtom);
  const setConfidenceScoreFilter = useSetAtom(confidenceScoreFilterAtom);
  const setHiddenAnnotations = useSetAtom(hiddenAnnotationsAtom);
  const setSelectedFindingImageId = useSetAtom(selectedFindingImageIdAtom);
  const setSelectedFindings = useSetAtom(selectedFindingsAtom);
  const setShouldDisplayRootTips = useSetAtom(shouldDisplayRootTipsAtom);
  const setViewerMode = useSetAtom(viewerModeAtom);
  const setSelectedAnalyses = useSetAtom(selectedAnalysesAtom);
  const setSelectedTeeth = useSetAtom(selectedTeethPreviewAtom);
  const setPatientDownloadableNotes = useSetAtom(patientDownloadableNotesAtom);
  const setSelectedVisit = useSetAtom(selectedVisitAtom);
  const setSelectedUnifiedId = useSetAtom(selectedUnifiedIdAtom);
  const setPatientDownloadableSelectedTreatments = useSetAtom(
    patientDownloadableSelectedTreatmentsAtom,
  );
  const setPreviewDisplayDevice = useSetAtom(previewDisplayDeviceAtom);
  const setPreviewMode = useSetAtom(previewModeAtom);
  const [isAtomsLoadedFromQRCode, setIsAtomsLoadedFromQRCode] = useState(false);

  useEffect(() => {
    if (!atomsFromQRCode) {
      return;
    }

    // Define atom mappings for cleaner code
    const atomMappings: {
      key: keyof PatientDownloadableAtoms;
      setter: (
        value: ExtractAtomValue<
          PatientDownloadableAtoms[keyof PatientDownloadableAtoms]
        >,
      ) => void;
    }[] = [
      { key: 'modelsToggles', setter: setModelsToggles },
      { key: 'hideFindings', setter: setHideFindings },
      { key: 'enabledHD', setter: setEnabledHD },
      { key: 'showSegmentation', setter: setShowSegmentation },
      { key: 'brightnessValue', setter: setBrightnessValue },
      { key: 'contrastValue', setter: setContrastValue },
      { key: 'isOpacityInverted', setter: setIsOpacityInverted },
      { key: 'toothPartsToggles', setter: setToothPartsToggles },
      { key: 'binFilter', setter: setBinFilter },
      { key: 'confidenceScoreFilter', setter: setConfidenceScoreFilter },
      { key: 'hiddenAnnotations', setter: setHiddenAnnotations },
      { key: 'selectedFindingImageId', setter: setSelectedFindingImageId },
      { key: 'selectedFindings', setter: setSelectedFindings },
      { key: 'shouldDisplayRootTips', setter: setShouldDisplayRootTips },
      { key: 'viewerMode', setter: setViewerMode },
      { key: 'selectedAnalyses', setter: setSelectedAnalyses },
      { key: 'selectedTeethPreview', setter: setSelectedTeeth },
      { key: 'patientDownloadableNotes', setter: setPatientDownloadableNotes },
      { key: 'estimatedVisitId', setter: setSelectedVisit },
      { key: 'selectedUnifiedId', setter: setSelectedUnifiedId },
      {
        key: 'patientDownloadableSelectedTreatments',
        setter: setPatientDownloadableSelectedTreatments,
      },
      {
        key: 'visitDate',
        setter: (val: string) => {
          setVisitDate(new Date(val));
        },
      },
    ];

    // Set atom values if they exist in the response
    atomMappings.forEach(({ key, setter }) => {
      const value = atomsFromQRCode?.[key] as ExtractAtomValue<
        PatientDownloadableAtoms[keyof PatientDownloadableAtoms]
      >;

      if (value !== undefined) {
        setter(value);
      }
    });

    // These aren't fetched from BE, these are just defaults
    setPreviewDisplayDevice('mobile');
    setPreviewMode('qrCode');

    // Mark atoms as loaded
    setIsAtomsLoadedFromQRCode(true);
  }, [
    atomsFromQRCode,
    setModelsToggles,
    setHideFindings,
    setEnabledHD,
    setShowSegmentation,
    setBrightnessValue,
    setContrastValue,
    setIsOpacityInverted,
    setToothPartsToggles,
    setBinFilter,
    setConfidenceScoreFilter,
    setHiddenAnnotations,
    setSelectedFindingImageId,
    setSelectedFindings,
    setShouldDisplayRootTips,
    setViewerMode,
    setSelectedAnalyses,
    setSelectedTeeth,
    setPatientDownloadableNotes,
    setSelectedVisit,
    setSelectedUnifiedId,
    setPatientDownloadableSelectedTreatments,
    setPreviewDisplayDevice,
    setPreviewMode,
    setVisitDate,
  ]);

  return isAtomsLoadedFromQRCode;
};

// Create an external state provider for the public route
export const PatientDownloadableStateProvider = ({
  children,
  atomsFromQRCode,
  rblTemplateName,
  practiceFeatureFlags,
  practiceCountry,
  patientInfo,
  salesDemoPracticeConfig,
}: {
  children: React.ReactNode;
  atomsFromQRCode?: PatientDownloadableAtoms;
  rblTemplateName?: string;
  practiceFeatureFlags?: {
    [key in PracticeFeatureFlagKey]?: boolean;
  };
  practiceCountry?: string;
  patientInfo: PublicPatientInfo;
  salesDemoPracticeConfig?: {
    practiceId: string;
    practiceName: string;
  };
}) => {
  // Create Redux store with initial state including RBL template name
  const store = configureStore({
    login: {
      user: {
        primaryPracticeName: patientInfo.practiceName,
        primaryPracticeId: patientInfo.practiceId,
        primaryOrganizationId: patientInfo.organizationId,
        primaryOrganizationName: patientInfo.organizationName,
        ...(process.env.VITE_SALES_DEMO_ENV && salesDemoPracticeConfig
          ? { salesDemoPracticeConfig }
          : {}),
        practice: {
          rblSettings: {
            templateName: rblTemplateName ?? 'default',
          },
          country: practiceCountry ?? 'US',
        },
        // Add practice feature flags for external case
        practiceFeatureFlags: Object.entries(practiceFeatureFlags ?? {}).map(
          ([key, value]) => ({
            key: key as PracticeFeatureFlagKey,
            value,
          }),
        ),
      },
    },
  });

  const isAtomsLoadedFromQRCode = useSetAtoms(atomsFromQRCode);

  // Don't render anything until atoms are loaded
  if (!isAtomsLoadedFromQRCode) {
    return (
      <Provider store={store}>
        <div className="vai-bg-black vai-flex vai-justify-center vai-items-center vai-w-full vai-h-screen">
          <div className="vai-w-[40%] max-md:vai-w-[90%] vai-bg-white vai-rounded-[1em] vai-p-[3em_3em_2em] vai-flex vai-flex-col vai-gap-8 vai-items-start">
            <VideaTypography variant="headingMedium" color={blueBlack630}>
              <Trans>Loading your settings...</Trans>
            </VideaTypography>
            <div className="vai-w-full">
              <LinearProgress />
            </div>
          </div>
        </div>
      </Provider>
    );
  }

  return <Provider store={store}>{children}</Provider>;
};
